import { FC, useState, useEffect, useContext, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { CourseEvent } from '../../../models/CourseEvent';
import DataTable, { DataTableColumn } from '../../widget/DataTable';
import { Waitlist } from '../../../models/Waitlist';
import Container from 'react-bootstrap/esm/Container';
import { getData } from '../../../utils/GetData';
import WaitlistEndpoint from '../../../../api/endpoint/WaitlistEndpoint';
import { SessionContext } from '../../../context/SessionContext';
import { displayDateTimeAsVtTime } from '../../../utils/DateManagement';

interface WaitlistViewProps {
    courseEvent: CourseEvent;
    show: boolean;
    onHide: () => void;
    initiateReload: () => void;
    initiateParentReload: () => void;
}

export const WaitlistView: FC<WaitlistViewProps> = ({courseEvent, show, onHide, initiateReload, initiateParentReload}) => {
    const [waitlist, setWaitlist] = useState<Waitlist[] | undefined>(undefined);

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState<{ column: string | null; direction: "asc" | "desc" | "none" }>({ column: null, direction: "asc" });
    const [pageSize, setPageSize] = useState(5);
    const [searchError, setSearchError] = useState<string | null>(null);

    const [needsReload, setNeedsReload] = useState(false);

    const sessionInfo = useContext(SessionContext);
    const sessionUser = sessionInfo?.user ? sessionInfo.user : null;

    const cols = useMemo< DataTableColumn<Waitlist>[]>(() => {
        const columns: DataTableColumn<Waitlist>[] = [
            {column: 'full_name', label: "Full Name", sortable:true, headerClassName:"sortable-header"},
            {column: 'email', label: "Email", sortable:true, headerClassName:"sortable-header"},
            {column: 'phone_number', label: "Phone Number", sortable:true, headerClassName:"sortable-header"},
            {column: 'submission_date', label: "Submission Date",  sortable:true, headerClassName:"sortable-header",
                accessor: (waitlist: Waitlist) => {
                    return (
                        <>{displayDateTimeAsVtTime(waitlist.submission_date!)}</>
                    );
                }
            }
        ];

        if(sessionUser && sessionUser.user_role === "Admin") {
            columns.push(
                {column: 'register', label: "Register",
                    accessor: (row: any) => {
                        return (
                            <Button variant="success" onClick={() => handleRegister(row)}>Register</Button>
                        );
                    }
                }
            );
        }

        return columns;
    }, [waitlist]);

    const handleRegister = async(waitlist: any) => {
        

        const registerResult = await WaitlistEndpoint.Register(waitlist);

        if(!registerResult || registerResult.errors.length > 0 || !registerResult.data){
            console.error('Error registering user');
        } else {
            // for waitlist table
            setNeedsReload(true);
            // for user registration table
            initiateReload();
            // for course event table
            initiateParentReload();
        }
    };

    const handleRegisterNext = async() => {

        const registerResult = await WaitlistEndpoint.RegisterNext(courseEvent.course_event_id);

        if(!registerResult || registerResult.errors.length > 0 || !registerResult.data){
            console.error('Error registering user');
        } else {
            // for waitlist table
            setNeedsReload(true);
            // for user registration table
            initiateReload();
        }
    };

    
    const getWaitlist = async () => {
        const result = await getData<Waitlist>(
            'api/waitlist/search',
            page,
            pageSize,
            {
                column: sort.column,
                direction: sort.direction,
                parameters: {'course_event_id': courseEvent.course_event_id}
            }
        );

        if(typeof result === 'object') {
            const waitlistResult: Waitlist[] = result.data.map((row: any) => ({
                course_event_waiting_list_id: Number(row.course_event_waiting_list_id),
                course_event_id: Number(row.course_event_id),
                full_name: row.full_name,
                email: row.email,
                phone_number: row.phone_number,
                submission_date: new Date(Date.parse(row.submission_date))
            }));
            setWaitlist(waitlistResult);
            setTotal(result.total);
            setSearchError(null);
        } else {
            setSearchError(result);
        }
        
    };

    useEffect(() => {
        if(show) {
            setNeedsReload(true);
        }
    }, [page, sort, pageSize, show]);

    useEffect(() => {
        if(needsReload) {
            getWaitlist();
            setNeedsReload(false);
        }
    }, [needsReload]);

    const handleSort = (col: DataTableColumn<Waitlist>) => {
            if (!col.sortable) return;
            let direction: "asc" | "desc" | "none" = "asc";
            if (sort.column === col.column) {
                if (sort.direction === "asc") {
                    direction = "desc";
                } else if (sort.direction === "desc") {
                    direction = "none";
                } else {
                    direction = "asc";
                }
            }
            const newSort = { column: direction === "none" ? null : col.column, direction };
            setSort(newSort);
            setPage(1);
            initiateReload();
        };

    return (
        <Modal
            show={show}
            size="lg"
            centered
            onHide={onHide}
        >
            <Modal.Header closeButton>
                <Container>
                    <Modal.Title>{courseEvent.title} Waitlist</Modal.Title>
                    {sessionUser && sessionUser.user_role === 'Admin' &&
                        <Button className="text-light mt-2" disabled={!waitlist || waitlist.length === 0} onClick={handleRegisterNext}>Register Next on List</Button>
                    }
                    </Container>
            </Modal.Header>
            
            <Modal.Body>
                {searchError &&
                    <div>
                        <strong>Search error:</strong> {searchError}
                    </div>
                }
                {waitlist && waitlist.length > 0 &&
                    <DataTable 
                        data={waitlist} 
                        columns={cols} 
                        id={`${courseEvent.course_event_id}_waitlist`}
                        page={page} 
                        pageSize={pageSize} 
                        totalRecords={total}
                        onPageChange={setPage}
                        handlePageSizeChange={(newPageSize: number) => {
                            setPageSize(newPageSize);
                            setPage(1);  // Reset to first page when changing items per page
                        }}
                        sort={sort}
                        onSort={handleSort}
                    />
                }
                {!waitlist || waitlist.length === 0 &&
                    <div>
                        No users in the waitlist.
                    </div>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}